import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, inject, provideAppInitializer } from '@angular/core';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { AuthService, CONFIG_TOKEN, ConsoleLoggerService, LoggerService } from '@app/shared/data';
import { environment } from '@env';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import { routes } from './app.routes';
import { AppStore } from './app.store';

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#f4f7f4',
      100: '#d9e6d9',
      200: '#bdd6bd',
      300: '#a2c6a2',
      400: '#86b686',
      500: '#284c24',
      600: '#214021',
      700: '#1a351a',
      800: '#132a13',
      900: '#0c1f0c',
    },
    green: {
      50: '#f4f7f4',
      100: '#d9e6d9',
      200: '#bdd6bd',
      300: '#a2c6a2',
      400: '#86b686',
      500: '#284c24',
      600: '#214021',
      700: '#1a351a',
      800: '#132a13',
      900: '#0c1f0c',
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '{neutral.50}',
          100: '{neutral.100}',
          200: '{neutral.200}',
          300: '{neutral.300}',
          400: '{neutral.400}',
          500: '{neutral.500}',
          600: '{neutral.600}',
          700: '{neutral.700}',
          800: '{neutral.800}',
          900: '{neutral.900}',
          950: '{neutral.950}',
        },
      },
      dark: {
        surface: {
          0: '#ffffff',
          50: '{neutral.50}',
          100: '{neutral.100}',
          200: '{neutral.200}',
          300: '{neutral.300}',
          400: '{neutral.400}',
          500: '{neutral.500}',
          600: '{neutral.600}',
          700: '{neutral.700}',
          800: '{neutral.800}',
          900: '{neutral.900}',
          950: '{neutral.950}',
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
    ),
    provideAnimationsAsync(),
    provideHttpClient(),
    provideFirebaseApp(() =>
      initializeApp({
        apiKey: environment.KEYS.firebase,
        ...environment.OPTIONS.firebase,
      }),
    ),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideFunctions(() => getFunctions(getApp())),
    provideStorage(() => getStorage()),
    provideAppInitializer(() => {
      inject(AppStore);
    }),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
    { provide: CONFIG_TOKEN, useValue: !environment.production },
    { provide: LoggerService, useClass: ConsoleLoggerService },
    AuthService,
    MessageService,
    ConfirmationService,
    DialogService,
  ],
};
